/**
 * Web Push subscription lifecycle.
 * pushState tracks where this device sits in the permission/subscription
 * flow. initPush() runs once after auth and silently re-subscribes devices
 * that already granted permission. enablePush() must be called from a user
 * gesture (browser requirement for Notification.requestPermission).
 */

import { signal } from '@preact/signals';
import { api } from '../api/client';

export type PushState =
  | 'unsupported'
  | 'default'
  | 'denied'
  | 'granted-unsubscribed'
  | 'subscribed';

export const pushState = signal<PushState>('default');

function pushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Decode a base64url VAPID key into the Uint8Array PushManager expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Race a promise against a timeout. Rejects with a labeled Error on timeout so
 * a hung or never-resolving service-worker registration can never wedge the
 * push flow (W4.1 deferred fix b). */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Compare two ArrayBuffer-like values byte-for-byte. Used to detect a VAPID
 * key rotation: an existing subscription bound to an old applicationServerKey
 * would 403 on every send (masked today by the Telegram fallback), so we must
 * unsubscribe + resubscribe under the current key (W4.1 deferred fix a). */
function buffersEqual(a: ArrayBuffer | null, b: ArrayBuffer | null): boolean {
  if (!a || !b) return false;
  if (a.byteLength !== b.byteLength) return false;
  const va = new Uint8Array(a);
  const vb = new Uint8Array(b);
  for (let i = 0; i < va.length; i++) {
    if (va[i] !== vb[i]) return false;
  }
  return true;
}

/**
 * Make sure this device has a live push subscription registered with the
 * backend. Never throws: failures are logged and leave the state at
 * 'granted-unsubscribed' so the banner can offer a retry.
 */
async function ensureSubscribed(): Promise<void> {
  try {
    // W4.1 fix (b): a rejected or hung service-worker registration must never
    // hang the push flow. Bound serviceWorker.ready to 10s; on timeout we log
    // and leave the device unsubscribed (Telegram fallback still covers alerts).
    const reg = await withTimeout(
      navigator.serviceWorker.ready,
      10_000,
      'serviceWorker.ready',
    );

    // Fetch the current VAPID public key once, up front, so we can both detect
    // a key rotation on an existing subscription and subscribe a fresh device.
    const { publicKey } = await api.getPushPublicKey();
    const currentKey = urlBase64ToUint8Array(publicKey);

    let sub = await reg.pushManager.getSubscription();

    // W4.1 fix (a): key rotation. If a subscription exists but was created under
    // a different applicationServerKey, every push 403s (silently, because of the
    // Telegram fallback). Unsubscribe the stale one and resubscribe under the
    // current key so push actually delivers again.
    if (sub) {
      const existingKey = sub.options?.applicationServerKey ?? null;
      if (!buffersEqual(existingKey, currentKey.buffer)) {
        console.warn('[push] VAPID key mismatch — re-subscribing under current key');
        try {
          await sub.unsubscribe();
        } catch (e) {
          console.warn('[push] unsubscribe of stale subscription failed:', e);
        }
        sub = null;
      }
    }

    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: currentKey,
      });
    }

    const json = sub.toJSON();
    const { endpoint, keys } = json;
    if (!endpoint || !keys?.p256dh || !keys?.auth) {
      throw new Error('push subscription missing endpoint or keys');
    }

    await api.subscribePush({
      endpoint,
      keys: { p256dh: keys.p256dh, auth: keys.auth },
      userAgent: navigator.userAgent,
    });
    pushState.value = 'subscribed';
  } catch (err) {
    console.error('[push] ensureSubscribed failed:', err);
    pushState.value = 'granted-unsubscribed';
  }
}

/**
 * Called once after auth. Detects support, then either silently restores
 * the subscription (permission already granted) or surfaces the current
 * permission so the banner can prompt.
 */
export async function initPush(): Promise<void> {
  if (!pushSupported()) {
    pushState.value = 'unsupported';
    return;
  }
  const permission = Notification.permission;
  if (permission === 'granted') {
    pushState.value = 'granted-unsubscribed';
    await ensureSubscribed();
  } else {
    pushState.value = permission;
  }
}

/**
 * Request notification permission and subscribe. MUST be called from a user
 * gesture or the browser rejects the permission prompt outright.
 */
export async function enablePush(): Promise<void> {
  if (!pushSupported()) {
    pushState.value = 'unsupported';
    return;
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      pushState.value = 'granted-unsubscribed';
      await ensureSubscribed();
    } else {
      pushState.value = permission;
    }
  } catch (err) {
    console.error('[push] enablePush failed:', err);
  }
}
